import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASEURL, SALESINVOICES } from "./../api/endpoints.js";
import getCookie from "../utils/getCookie.js";

const normalizeInvoiceDetail = (item = {}) => ({
  ...item,
  ItemCode:
    item.ItemCode !== undefined && item.ItemCode !== null
      ? String(item.ItemCode)
      : item.ItemID !== undefined && item.ItemID !== null
        ? String(item.ItemID)
        : "",
  ItemDescAr:
    item.ItemDescAr ?? item.ItemDesc ?? item.ItemDescEn ?? item.NameAr ?? item.NameEn ?? "",
  ItemDescEn:
    item.ItemDescEn ?? item.ItemDesc ?? item.ItemDescAr ?? item.NameEn ?? item.NameAr ?? "",
  UnitId: item.UnitId ?? item.UnitID ?? item.Unit ?? "",
  UnitID: item.UnitID ?? item.UnitId ?? item.Unit ?? "",
  UnitDescAr: item.UnitDescAr ?? item.UnitDesc ?? item.UnitDescEn ?? "",
  UnitDescEn: item.UnitDescEn ?? item.UnitDesc ?? item.UnitDescAr ?? "",
  Qty: Number(item.Qty ?? 0),
  BonusQty: Number(item.BonusQty ?? 0),
  Price: Number(item.Price ?? 0),
  Discount: Number(item.Discount ?? 0),
  Tax: Number(item.Tax ?? item.TaxAmount ?? 0),
  TaxPercentage: Number(item.TaxPercentage ?? item.TaxPer ?? 0),
  TaxPer: Number(item.TaxPer ?? item.TaxPercentage ?? 0),
  NetTotal: Number(item.NetTotal ?? item.Total ?? 0),
});

export const salesInvoiceApi = createApi({
  reducerPath: "salesInvoiceApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASEURL}${SALESINVOICES}`,
    prepareHeaders: (headers) => {
      headers.set("Authorization", `Bearer ${getCookie("accessToken")}`);
      return headers;
    },
  }),
  tagTypes: ["SalesInvoice"],
  endpoints: (builder) => ({
    
    // 1. جلب قائمة الفواتير
    getSalesInvoices: builder.query({
      query: (args) => ({
        url: "/GetAll",
        params: {
          "paging.PageNumber": args?.pageNumber || 1,
          "paging.PageSize": args?.pageSize || 10,
        },
      }),
      transformResponse: (response) => {
        // التأكد من الوصول للمصفوفة داخل Response
        if (response?.Response && Array.isArray(response.Response)) {
          return response.Response.map((invoice) => ({
            ...invoice,
            InvoiceDate: invoice.InvoiceDate
              ? new Date(invoice.InvoiceDate).toLocaleDateString("en-CA")
              : null,
          }));
        }
        return [];
      },
      providesTags: (result) =>
        result
          ? [...result.map(({ InvoiceNo }) => ({ type: "SalesInvoice", id: InvoiceNo })), { type: "SalesInvoice", id: "LIST" }]
          : [{ type: "SalesInvoice", id: "LIST" }],
    }),

    // 2. جلب تفاصيل فاتورة محددة (الأسطر فقط)
    getInvoiceDetails: builder.query({
      query: (invoiceNo) => ({
        url: `/GetDetails`,
        params: { invoiceNo } // الأفضل تمريرها كـ params
      }),
      transformResponse: (response) => {
        // GetDetails returns ItemDesc/UnitDesc, while the editable grid uses
        // ItemDescAr/UnitDescAr. Normalize both naming variants here.
        const details = response?.Response?.Details || [];
        return Array.isArray(details) ? details.map(normalizeInvoiceDetail) : [];
      },
      providesTags: (result, error, invoiceNo) => [{ type: "SalesInvoice", id: invoiceNo }],
    }),

    // 3. جلب الفاتورة كاملة (Header + Details)
    getFullInvoiceData: builder.query({
      query: (invoiceNo) => ({
        url: `/GetDetails`,
        params: { invoiceNo }
      }),
      transformResponse: (response) => {
        const data = response?.Response;
        if (!data) return null;

        return {
          ...data,
          Header: data.Header
            ? {
                ...data.Header,
                InvoiceDate: data.Header.InvoiceDate
                  ? String(data.Header.InvoiceDate).slice(0, 10)
                  : "",
              }
            : null,
          Details: Array.isArray(data.Details)
            ? data.Details.map(normalizeInvoiceDetail)
            : [],
        };
      },
      providesTags: (result, error, invoiceNo) => [{ type: "SalesInvoice", id: invoiceNo }],
    }),

    // Mutations
    addSalesInvoice: builder.mutation({
      query: (data) => ({
        url: "/Insert",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "SalesInvoice", id: "LIST" }],
    }),

    updateSalesInvoice: builder.mutation({
      query: (data) => ({
        url: "/Update",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, data) => {
        const invoiceNo = data?.Header?.InvoiceNo ?? data?.InvoiceNo;
        return [
          ...(invoiceNo !== undefined && invoiceNo !== null
            ? [{ type: "SalesInvoice", id: invoiceNo }]
            : []),
          { type: "SalesInvoice", id: "LIST" }
        ];
      },
    }),

    // Re-send a sales invoice to the tax/Fotara service.
    // It is a mutation hook so the call only happens when the user clicks.
    reSendFotara: builder.mutation({
      query: (invoiceNo) => ({
        url: "/ReSendFotara",
        method: "GET",
        params: { invoiceNo },
      }),
      invalidatesTags: (result, error, invoiceNo) => [
        { type: "SalesInvoice", id: invoiceNo },
      ],
    }),

    deleteSalesInvoice: builder.mutation({
      query: (args) => {
        let invoiceNo = typeof args === "object" ? (args.invoiceNo || args.InvoiceNo) : args;
        return {
          url: `/Delete`,
          method: "POST",
          params: { invoiceNo }
        };
      },
      invalidatesTags: [{ type: "SalesInvoice", id: "LIST" }],
    }),
  }),
});

export const {
  useGetSalesInvoicesQuery,
  useGetInvoiceDetailsQuery,
  useGetFullInvoiceDataQuery,
  useAddSalesInvoiceMutation,
  useUpdateSalesInvoiceMutation,
  useReSendFotaraMutation,
  useDeleteSalesInvoiceMutation,
} = salesInvoiceApi;